import { useEffect, useState, type ImgHTMLAttributes } from "react";

/** A renewed R2 signature identifies the same photo. Keep content/version
 * parameters in the identity so an annotation or a different photo changes
 * immediately instead of temporarily displaying the previous photograph. */
function photoResource(src: string | undefined): string | undefined {
  if (!src) return src;
  try {
    const url = new URL(src);
    if (!url.searchParams.has("X-Amz-Signature")) return src;
    for (const name of [...url.searchParams.keys()]) {
      if (/^X-Amz-(Algorithm|Credential|Date|Expires|Security-Token|Signature|SignedHeaders)$/i.test(name)) {
        url.searchParams.delete(name);
      }
    }
    url.searchParams.sort();
    return url.href;
  } catch { return src; }
}

/** Photo URLs are signed again on background reads. Preserve the displayed
 * image while its renewed URL loads and decodes; an offscreen photo that has
 * never loaded still uses the browser's normal lazy-loading behavior. */
export function PhotoImage({ src, onLoad, onError, ...props }: ImgHTMLAttributes<HTMLImageElement>) {
  const resource = photoResource(src);
  const [loaded, setLoaded] = useState<{ src: string; resource: string } | null>(null);
  const retained = loaded?.resource === resource ? loaded : null;
  const displaySrc = retained?.src ?? src;

  useEffect(() => {
    // Never retain an image through source removal or navigation to another
    // photo, even if the component is subsequently given the old source again.
    if (loaded && loaded.resource !== resource) setLoaded(null);
    if (!src || !resource || !retained || retained.src === src) return;

    let cancelled = false;
    const replacement = new Image();
    replacement.onload = () => {
      void (async () => {
        try {
          await replacement.decode();
          if (!cancelled) setLoaded({ src, resource });
        } catch { /* Keep the already-loaded photo if the replacement fails. */ }
      })();
    };
    replacement.src = src;
    return () => {
      cancelled = true;
      replacement.onload = null;
    };
  }, [src, resource, loaded, retained]);

  return <img {...props} src={displaySrc} onLoad={event => {
    if (displaySrc && resource) setLoaded(previous => previous?.src === displaySrc && previous.resource === resource
      ? previous : { src: displaySrc, resource });
    onLoad?.(event);
  }} onError={event => {
    setLoaded(null);
    onError?.(event);
  }} />;
}
